import { createContext, useEffect, useMemo, useState } from "react";

import { useGlobal } from "./globalContext";

/*
  To use this context, just use the useUserMediaPreviewStreamConsumer or useUserMedia hooks and avoid importing and using the userMediaContext directly from this file.
  - This is the single source of truth to interact with user media stream and tracks across the app.
  - Helps maintaining a single user media stream (with active tracks) for the entire app
  - Currently only used for preview stream, used by device selection / call setting components
  - For the main publishing stream use MEETING_STATE.webcamStream & MEETING_STATE.micStream from the global context.
    These are kept in sync with janus's tracks in videoroomtest.js (on videoroom plugin event handlers, defined when attaching the plugin)
  - Alternatively we can also maintain the main publishing stream in here similar to the preview stream, and then we can give janus our stream (as an external stream),
    instead of asking janus to maintain its own stream by providing constraints. But we didn't do that because janus maintains the stream better.
*/
export const userMediaContext = createContext({});
export default function UserMediaContextProvider({ children }) {
  const { MEETING_STATE } = useGlobal();

  const [
    previewStream,
    previewCamStream,
    previewMicStream,
    startPreviewStream,
    switchPreviewCamDevice,
    switchPreviewMicDevice,
    previewStreamCalled,
    resetPreviewStreamCallStatus,
    stopPreviewStream,
    stopPreviewCamStream,
    stopPreviewMicStream,
    previewCamDeviceId,
    previewMicDeviceId,
  ] = useUserMediaStream();

  const [previewStreamConsumers, setPreviewStreamConsumers] = useState(0);
  useEffect(() => {
    if (previewStreamConsumers > 0) {
      // first preview stream consumer mounted
      startPreviewStream();
    }

    if (previewStreamConsumers === 0) {
      // last preview stream consumer unmounting
      stopPreviewStream();
    }
  }, [previewStreamConsumers]);

  const { camDevices, micDevices } = useGetDevices(previewStream);

  const savePreviewDeviceSelections = () => {
    saveDeviceSelections(previewCamDeviceId, previewMicDeviceId);
    if (MEETING_STATE.webcamStream) {
      if (getDeviceId(MEETING_STATE.webcamStream.getVideoTracks()[0]) !== previewCamDeviceId) console.log("TODO: SWITCH janus stream cam device");
    }

    if (MEETING_STATE.micStream) {
      if (getDeviceId(MEETING_STATE.micStream.getAudioTracks()[0]) !== previewMicDeviceId) console.log("TODO: SWITCH janus stream mic device");
    }

    // TODO: update janus to use the new devices
  };

  const contextValue = useMemo(
    () => ({
      previewStream,
      previewCamStream,
      previewMicStream,
      switchPreviewCamDevice,
      switchPreviewMicDevice,
      previewStreamCalled,
      resetPreviewStreamCallStatus,
      previewCamDeviceId,
      previewMicDeviceId,
      camDevices,
      micDevices,
      savePreviewDeviceSelections,
      startPreviewStream,
      stopPreviewStream,
      stopPreviewCamStream,
      stopPreviewMicStream,
      previewStreamConsumers,
      setPreviewStreamConsumers,
    }),
    [
      previewStream,
      previewCamStream,
      previewMicStream,
      previewStreamCalled,
      previewCamDeviceId,
      previewMicDeviceId,
      camDevices,
      micDevices,
      previewStreamConsumers,
    ],
  );

  return <userMediaContext.Provider value={contextValue}>{children}</userMediaContext.Provider>;
}

function useUserMediaStream() {
  const [streamCalled, setStreamCalled] = useState(false); // false: stream not called, true: stream called and
  // waiting, error: stream called & got rejected with this
  // error
  const resetStreamCallStatus = () => setStreamCalled(false); // used to remove the error after the user has seen it

  const [videoTrack, setVideoTrack] = useState();
  const [audioTrack, setAudioTrack] = useState();

  const [camStream, setCamStream] = useState();
  const [micStream, setMicStream] = useState();
  const [stream, setStream] = useState();

  const [camDeviceId, setCamDeviceId] = useState();
  const [micDeviceId, setMicDeviceId] = useState();

  const startUserMedia = async (constraints) => {
    if (streamCalled === true) {
      const err = new Error("New stream requested while there is another unfulfilled request waiting for user action");
      console.error("💀 UsageError (useUserMediaStream):", err);
      throw err;
    }
    setStreamCalled(true);
    return getUserMedia(constraints)
      .catch((error) => {
        setStreamCalled(error);
        throw error;
      })
      .then((userMedia) => {
        setStreamCalled(false);
        const newVideoTrack = userMedia.getVideoTracks()[0];
        const newAudioTrack = userMedia.getAudioTracks()[0];
        if (videoTrack && videoTrack.readyState === "live" && newVideoTrack) {
          console.warn("💀 UsageError (useUserMediaStream): New video track requested while the previous one is still active");
        }

        if (audioTrack && audioTrack.readyState === "live" && newAudioTrack) {
          console.warn("💀 UsageError (useUserMediaStream): New audio track requested while the previous one is still active");
        }

        if (newVideoTrack) setVideoTrack(newVideoTrack);

        if (newAudioTrack) setAudioTrack(newAudioTrack);
      });
  };
  const switchCamDevice = async (deviceId) => {
    if (camDeviceId === deviceId) return;

    stopCam();
    return startUserMedia({ audio: false, camDeviceId: deviceId });
    // alternative option is to stop both Cam and Mic, then request for audio track too. might help to reduce
    // audio/video delays.
  };
  const switchMicDevice = async (deviceId) => {
    if (micDeviceId === deviceId) return;

    stopMic();
    return startUserMedia({ video: false, micDeviceId: deviceId });
    // alternative option is to stop both Cam and Mic, then request for video track too. might help to reduce
    // audio/video delays.
  };

  const stop = () => {
    stopCam();
    stopMic();
  };
  const stopCam = () => {
    if (!videoTrack) return;
    videoTrack.stop();
    setVideoTrack();
  };
  const stopMic = () => {
    if (!audioTrack) return;
    audioTrack.stop();
    setAudioTrack();
  };

  useEffect(() => {
    const currentVideoTrack = camStream && camStream.getVideoTracks()[0];
    const currentAudioTrack = micStream && micStream.getAudioTracks()[0];
    const videoTrackChanged = videoTrack !== currentVideoTrack;
    const audioTrackChanged = audioTrack !== currentAudioTrack;
    if (videoTrackChanged) {
      setCamStream(videoTrack && new MediaStream([videoTrack]));
      setCamDeviceId(getDeviceId(videoTrack));
    }
    if (audioTrackChanged) {
      setMicStream(audioTrack && new MediaStream([audioTrack]));
      setMicDeviceId(getDeviceId(audioTrack));
    }
    if (videoTrackChanged || audioTrackChanged) {
      const allNewTracks = [];
      if (videoTrackChanged && videoTrack) allNewTracks.push(videoTrack);
      if (!videoTrackChanged && currentVideoTrack) allNewTracks.push(currentVideoTrack);
      if (audioTrackChanged && audioTrack) allNewTracks.push(audioTrack);
      if (!audioTrackChanged && currentAudioTrack) allNewTracks.push(currentAudioTrack);
      setStream(allNewTracks.length ? new MediaStream(allNewTracks) : undefined);
    }
  }, [videoTrack, audioTrack]);

  return [
    stream,
    camStream,
    micStream,
    startUserMedia,
    switchCamDevice,
    switchMicDevice,
    streamCalled,
    resetStreamCallStatus,
    stop,
    stopCam,
    stopMic,
    camDeviceId,
    micDeviceId,
  ];
}

function useGetDevices(stream) {
  const [initialDeviceListReported, setInitialDeviceListReported] = useState(false);
  const [camDevices, setCamDevices] = useState([]);
  const [micDevices, setMicDevices] = useState([]);
  useEffect(() => {
    if (!initialDeviceListReported && stream) {
      // TODO: add listener for device changes & removeListener on effect return
      getDevices().then(({ cams, mics }) => {
        setCamDevices(cams);
        setMicDevices(mics);
      });
      setInitialDeviceListReported(true);
    }
  }, [stream, initialDeviceListReported]);
  return { camDevices, micDevices };
}

export function getMediaConstraints({ audio = {}, video = { aspectRatio: { exact: 4 / 3 } }, camDeviceId, micDeviceId } = {}) {
  const { cam: lastCamDeviceId, mic: lastMicDeviceId } = getSavedDeviceSelections();
  if (video) {
    const ideal = camDeviceId || lastCamDeviceId;
    if (ideal) video.deviceId = { ideal };
  }
  if (audio) {
    const ideal = micDeviceId || lastMicDeviceId;
    if (ideal) audio.deviceId = { ideal };
  }
  return { audio, video };
}

async function getUserMedia(constraints) {
  const constructedConstraints = getMediaConstraints(constraints);
  return navigator.mediaDevices.getUserMedia(constructedConstraints);
}

async function getDevices() {
  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);

  const cams = [];
  const mics = [];

  devices.forEach((device) => {
    const { deviceId, label, kind } = device;
    if (!deviceId || !label || deviceId === "default" || deviceId === "communications") return;

    if (kind === "videoinput") {
      let supported = null;
      if (device.getCapabilities) {
        const capabilities = device.getCapabilities();
        if (capabilities.aspectRatio && capabilities.aspectRatio.max && capabilities.aspectRatio.min) {
          supported = capabilities.aspectRatio.max > 4 / 3 && capabilities.aspectRatio.min < 4 / 3;
        }
      }
      cams.push({ deviceId, label, supported });
    }

    if (kind === "audioinput") mics.push({ deviceId, label, supported: true });
  });

  return { cams, mics };
}

function getDeviceId(track) {
  return track?.getSettings()?.deviceId;
}

function saveDeviceSelections(cam, mic) {
  localStorage.setItem("selectedDeviceIds", JSON.stringify({ cam, mic }));
}

function getSavedDeviceSelections() {
  const selectedDeviceIds = localStorage.getItem("selectedDeviceIds") || "{}";
  const { cam, mic } = JSON.parse(selectedDeviceIds);
  return { cam, mic };
}
